"use client";

import { useEffect, useState } from "react";
import Link from "next/link";
import Image from "next/image";
import { Star, ChevronRight, Search } from "lucide-react";
import { useFavorieten } from "@/components/favorieten";
import { favorietHref, type Sectie } from "@/lib/favorieten";
import { datumInJaar } from "@/lib/format";

type TeamInfo = {
  sectie: Sectie;
  id: number;
  titel: string;
  ondertitel: string;
  foto: string | null;
  klasse: string | null;
  wedstrijd: { id: number; naam: string; datum: string } | null;
};

// De teams die je volgt, met hun trekker, hun klasse en hun eerstvolgende
// wedstrijd.
//
// De favorieten staan op het apparaat, dus de server weet niet wie je volgt.
// Daarom sturen we de lijst op en krijgen we terug wat alleen daar bekend is.
export function MijnTeams() {
  const { teams: gevolgdeTeams, geladen } = useFavorieten();
  const [teams, setTeams] = useState<TeamInfo[] | null>(null);

  useEffect(() => {
    if (!geladen || gevolgdeTeams.length === 0) return;

    const afbreken = new AbortController();
    fetch("/api/mijn-teams", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(gevolgdeTeams.map((f) => ({ sectie: f.sectie, id: f.id }))),
      signal: afbreken.signal,
    })
      .then((r) => (r.ok ? r.json() : []))
      .then((d: TeamInfo[]) => setTeams(d))
      .catch(() => {
        // Netwerkfout: dan tonen we hieronder gewoon de namen die we al hebben.
      });

    return () => afbreken.abort();
  }, [gevolgdeTeams, geladen]);

  // Vóór het lezen van de opslag niets tonen, anders flitst de uitnodiging om
  // teams te volgen voorbij bij iemand die er al tien heeft.
  if (!geladen) return null;

  if (gevolgdeTeams.length === 0) {
    return (
      <div className="mx-4 flex flex-col items-center gap-3 rounded-xl border border-dashed border-white/15 px-6 py-8 text-center">
        <Star className="size-6 text-muted-foreground" aria-hidden />
        <p className="text-sm text-muted-foreground">
          Volg je favoriete teams en zie hier wanneer ze weer rijden.
        </p>
        <Link
          href="/zoeken"
          className="inline-flex items-center gap-1.5 rounded-full bg-primary px-4 py-2 font-display text-sm font-black uppercase tracking-wide text-black"
        >
          <Search className="size-4" aria-hidden />
          Teams zoeken
        </Link>
      </div>
    );
  }

  // Welke teams er staan bepalen de favorieten, de details komen van de
  // server. Zo afgeleid in plaats van bijgehouden: verwijder je een team, dan
  // is het meteen weg zonder dat er state opgeruimd hoeft te worden, en tot
  // het antwoord er is staan de namen er al die we zelf bewaarden.
  const detail = new Map((teams ?? []).map((t) => [`${t.sectie}-${t.id}`, t]));
  const regels: TeamInfo[] = gevolgdeTeams.map(
    (f) =>
      detail.get(`${f.sectie}-${f.id}`) ?? {
        sectie: f.sectie,
        id: f.id,
        titel: f.naam,
        ondertitel: "",
        foto: null,
        klasse: null,
        wedstrijd: null,
      },
  );

  return (
    <ul className="flex snap-x snap-mandatory gap-3 overflow-x-auto px-4 pb-1">
      {regels.map((t) => (
        <li key={`${t.sectie}-${t.id}`} className="w-56 shrink-0 snap-start last:mr-4">
          <Link
            href={favorietHref({ soort: "team", sectie: t.sectie, id: t.id, naam: t.titel })}
            className="flex h-full flex-col overflow-hidden rounded-xl border border-[#2a4a6f] bg-card transition-colors active:bg-white/5"
          >
            <TeamFoto foto={t.foto} titel={t.titel} klasse={t.klasse} />

            <span className="flex flex-1 flex-col justify-between gap-2 p-3">
              <span className="min-w-0">
                <span className="block truncate font-semibold text-white">{t.titel}</span>
                {t.ondertitel && (
                  <span className="block truncate text-xs text-muted-foreground">{t.ondertitel}</span>
                )}
              </span>

              <span className="flex items-center gap-1 border-t border-white/10 pt-2 text-xs">
                {t.wedstrijd ? (
                  <>
                    <span className="min-w-0 flex-1 truncate text-soft-foreground">
                      <span className="font-semibold text-primary">
                        {datumInJaar(new Date(t.wedstrijd.datum))}
                      </span>{" "}
                      {t.wedstrijd.naam}
                    </span>
                    <ChevronRight className="size-3.5 shrink-0 text-white/30" aria-hidden />
                  </>
                ) : (
                  <span className="text-muted-foreground">Nog niet ingeschreven</span>
                )}
              </span>
            </span>
          </Link>
        </li>
      ))}
    </ul>
  );
}

/** Initialen uit de teamnaam, voor als er geen foto is. */
function initialenVan(naam: string): string {
  return naam
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() ?? "")
    .join("");
}

// De trekkerfoto boven de tegel, met de ster en de klasse eroverheen.
//
// Bij Farm Stock heeft maar ongeveer een op de vier machines een foto, dus de
// terugval is hier de normale toestand en geen uitzondering: dezelfde hoogte,
// met de initialen groot. Zo blijft de rij tegels recht.
function TeamFoto({
  foto,
  titel,
  klasse,
}: {
  foto: string | null;
  titel: string;
  klasse: string | null;
}) {
  const [fout, setFout] = useState(false);
  const heeftFoto = !!foto && !fout;

  return (
    <span className="relative block aspect-[4/3] w-full overflow-hidden bg-[#0f2233]">
      {heeftFoto ? (
        <Image
          src={foto}
          alt={`Foto van ${titel}`}
          fill
          sizes="224px"
          onError={() => setFout(true)}
          className="object-cover"
        />
      ) : (
        <span className="flex size-full items-center justify-center font-display text-3xl font-black text-primary/40">
          {initialenVan(titel) || "?"}
        </span>
      )}

      <Star
        className="absolute left-2 top-2 size-4 fill-primary text-primary drop-shadow"
        aria-hidden
      />

      {klasse && (
        <span className="absolute inset-x-0 bottom-0 truncate bg-gradient-to-t from-black/80 to-transparent px-2 pb-1.5 pt-4 text-[0.65rem] font-bold uppercase tracking-wide text-white">
          {klasse}
        </span>
      )}
    </span>
  );
}
